import { useState } from "react";
import { z } from "zod";
import type { AlteracaoDeConvenio, BancoDoCatalogo, Convenio, ConvenioNovo } from "@/api/contrato";
import { FalhaDaApi } from "@/api/problema";
import type { Matiz } from "@/componentes/PilulaDeSituacao";
import { banco } from "@/formato";

/*
 * O convênio no painel: o que o banco usa, o formulário e os corpos.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ O SEQUENCIAL NÃO É CAMPO DESTE FORMULÁRIO, E É DE PROPÓSITO.             │
 * │                                                                          │
 * │ Repetir nosso número é o risco existencial do produto. O próximo nosso   │
 * │ número existe só em `NovoConvenio`, por fora de `ValoresDoConvenio`, e   │
 * │ `AlteracaoDeConvenio` não tem a chave: editar o sequencial não é uma     │
 * │ regra que alguém possa esquecer, é um corpo que não compila.             │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * A validação daqui espelha o catálogo, e não o substitui: ela existe para o operador
 * ver o erro no campo antes da viagem. Quem decide é `domain.ValidarConvenioContraOBanco`.
 */

export type ModoDeRegistro = NonNullable<ConvenioNovo["modo_registro"]>;

export const MODOS_DE_REGISTRO: readonly (readonly [ModoDeRegistro, string])[] = [
  ["webservice", "WebService"],
  ["misto", "Misto"],
  ["automatico_van", "Automático por VAN"],
  ["manual", "Manual"],
];

export function rotuloDoModo(modo: string | undefined): string | null {
  if (!modo) return null;
  // O modo é enum do banco de dados. Um valor novo aparece cru, e não some.
  return MODOS_DE_REGISTRO.find(([valor]) => valor === modo)?.[1] ?? modo;
}

/**
 * O modo é a declaração do operador, e `registro_instantaneo` é o espelho dela
 * (design D10): por isso o formulário não oferece o booleano para escolha.
 */
export function usaWebService(modo: string | undefined): boolean {
  return modo === "webservice" || modo === "misto";
}

const MATIZ_DO_STATUS: Readonly<Record<string, Matiz>> = {
  ativo: "pago",
  provisionando: "transito",
  pendente_credencial: "transito",
  bloqueado: "encerrado",
};

export function matizDoStatus(status: string): Matiz {
  return MATIZ_DO_STATUS[status] ?? "neutro";
}

export function rotuloDoStatus(status: string): string {
  return status.replace("_", " de ");
}

/** Com a conta inativa, desbloquear não devolve a emissão, e a confirmação não pode prometer. */
export function corpoDoDesbloqueio(contaInativa: boolean): string {
  return contaInativa
    ? "A conta segue inativa: a emissão só volta quando ela for reativada."
    : "As emissões por este convênio voltam a ser aceitas.";
}

/** `3021-5`. `null` sem número, para `CaixaDeCampo` mostrar o travessão. */
export function comDigito(numero: string | undefined, digito: string | undefined): string | null {
  if (!numero) return null;
  return digito ? `${numero}-${digito}` : numero;
}

/** A linha de dado sob o título do modal: o banco e a conta vêm da linha, só leitura. */
export function linhaDaConta(conta: {
  banco: string;
  agencia?: string | undefined;
  agencia_dv?: string | undefined;
  conta?: string | undefined;
  conta_dv?: string | undefined;
}): string {
  const agencia = comDigito(conta.agencia, conta.agencia_dv) ?? "—";
  const numero = comDigito(conta.conta, conta.conta_dv) ?? "—";
  return `${banco(conta.banco)} · ${agencia} / ${numero}`;
}

/* ── O que o banco usa ───────────────────────────────────────────────────── */

export interface RegrasDoConvenio {
  /** Vazio é "não declarado", e não "nenhuma": a carteira vira texto livre. */
  carteiras: readonly string[];
  exigeCNAB: boolean;
  cnabComWebService: "" | "240" | "400";
  tipoWebService: string;
  /** O convênio só aceita `tipoWebService`, sem distinção de caixa. */
  tipoWebServiceFixo: boolean;
  /** Condicional: vale com WebService, e em CNAB é opcional. */
  exigeContrato: boolean;
  exigeRemessa: boolean;
  usaByte: boolean;
  /** Zero é sem limite declarado. O Sicredi aceita 5. */
  maxDigitosNossoNumero: number;
  orientacao: string | undefined;
}

/** Banco fora do catálogo, ou catálogo que não carregou, não tem regra: regra ausente não é regra zero. */
export function regrasDoBanco(linha: BancoDoCatalogo | undefined): RegrasDoConvenio {
  return {
    carteiras: linha?.carteiras_aceitas ?? [],
    exigeCNAB: linha?.exige_padrao_cnab === true,
    cnabComWebService: linha?.cnab_com_webservice ?? "",
    tipoWebService: linha?.tipo_webservice ?? "",
    tipoWebServiceFixo: linha?.exige_tipo_webservice === true,
    exigeContrato: linha?.exige_contrato_com_webservice === true,
    exigeRemessa: linha?.exige_numero_remessa === true,
    usaByte: linha?.usa_byte_nosso_numero === true,
    maxDigitosNossoNumero: linha?.max_digitos_nosso_numero ?? 0,
    orientacao: linha?.orientacao_convenio,
  };
}

type CamposOpcionais = Pick<Convenio, "numero_contrato" | "numero_remessa" | "byte_nosso_numero">;

/**
 * Os campos que só alguns bancos usam.
 *
 * O valor GRAVADO também mostra o campo: dado que existe não some da tela, e o operador
 * precisa poder limpá-lo. É o gravado, e não o digitado, ou o campo sumiria debaixo do
 * cursor ao ser apagado.
 */
export function camposDoBanco(regras: RegrasDoConvenio, gravado?: CamposOpcionais) {
  return {
    contrato: regras.exigeContrato || Boolean(gravado?.numero_contrato),
    remessa: regras.exigeRemessa || Boolean(gravado?.numero_remessa),
    byte: regras.usaByte || Boolean(gravado?.byte_nosso_numero),
  };
}

/* ── Formulário ──────────────────────────────────────────────────────────── */

/* As chaves SÃO os nomes da API: a recusa do servidor aponta o campo por esse nome. */
export interface ValoresDoConvenio {
  numero: string;
  carteira: string;
  variacao: string;
  descricao: string;
  modo_registro: ModoDeRegistro;
  padrao_cnab: string;
  tipo_webservice: string;
  numero_contrato: string;
  numero_remessa: string;
  reiniciar_remessa_diariamente: boolean;
  byte_nosso_numero: string;
}

export type CampoDoConvenio = keyof ValoresDoConvenio;
export type ErrosDoConvenio = Partial<Record<CampoDoConvenio, string | undefined>>;

/*
 * O que viaja no corpo da emissão (`tecnospeed/emissao.go`), e por isso fecha com a
 * janela (design D8). O resto é espelho do cadastro no provider, e fica aberto.
 */
export const CAMPOS_QUE_VIAJAM = ["numero", "carteira", "variacao", "byte_nosso_numero"] as const;

/** Nascem pelo catálogo. Número do convênio e contrato NÃO vêm pré-preenchidos (spec). */
export function valoresIniciais(regras: RegrasDoConvenio): ValoresDoConvenio {
  const [primeira, ...outras] = regras.carteiras;

  return {
    numero: "",
    carteira: primeira !== undefined && outras.length === 0 ? primeira : "",
    variacao: "",
    descricao: "",
    modo_registro: "webservice",
    padrao_cnab: regras.cnabComWebService,
    tipo_webservice: regras.tipoWebService,
    numero_contrato: "",
    numero_remessa: "",
    reiniciar_remessa_diariamente: false,
    byte_nosso_numero: "",
  };
}

function modoGravado(convenio: Convenio): ModoDeRegistro {
  return MODOS_DE_REGISTRO.find(([modo]) => modo === convenio.modo_registro)?.[0] ?? "webservice";
}

/** O convênio gravado, sem nada preenchido pelo catálogo: valor novo aqui viraria alteração. */
export function valoresDe(convenio: Convenio): ValoresDoConvenio {
  return {
    numero: convenio.numero,
    carteira: convenio.carteira,
    variacao: convenio.variacao ?? "",
    descricao: convenio.descricao ?? "",
    modo_registro: modoGravado(convenio),
    padrao_cnab: convenio.padrao_cnab ?? "",
    tipo_webservice: convenio.tipo_webservice ?? "",
    numero_contrato: convenio.numero_contrato ?? "",
    numero_remessa: convenio.numero_remessa ?? "",
    reiniciar_remessa_diariamente: convenio.reiniciar_remessa_diariamente === true,
    byte_nosso_numero: convenio.byte_nosso_numero ?? "",
  };
}

/** Trocar o modo leva junto o que depende dele: tipo de WebService e CNAB do Sicredi. */
function comMudanca<C extends CampoDoConvenio>(
  valores: ValoresDoConvenio,
  campo: C,
  valor: ValoresDoConvenio[C],
  regras: RegrasDoConvenio,
): ValoresDoConvenio {
  const proximos = { ...valores, [campo]: valor };
  if (campo !== "modo_registro") return proximos;

  const webService = usaWebService(proximos.modo_registro);
  return {
    ...proximos,
    tipo_webservice: webService ? regras.tipoWebService || valores.tipo_webservice : "",
    padrao_cnab:
      webService && regras.cnabComWebService !== "" ? regras.cnabComWebService : valores.padrao_cnab,
  };
}

/**
 * Os campos e os erros de um formulário de convênio, iguais na criação e na edição.
 *
 * Até a primeira mudança, os valores são os `iniciais` do render: conta e catálogo chegam
 * com o modal já aberto, e guardar o inicial no estado obrigaria a montar outro modal
 * quando eles chegassem, com o foco perdido e o diálogo piscando.
 */
export function useValoresDoConvenio(iniciais: ValoresDoConvenio, regras: RegrasDoConvenio) {
  const [editados, setEditados] = useState<ValoresDoConvenio | null>(null);
  const [erros, setErros] = useState<ErrosDoConvenio>({});

  function mudar<C extends CampoDoConvenio>(campo: C, valor: ValoresDoConvenio[C]) {
    setEditados((atuais) => comMudanca(atuais ?? iniciais, campo, valor, regras));
    // O erro some quando o operador mexe no campo. Mudar o modo muda o que é exigido.
    setErros((atuais) => (campo === "modo_registro" ? {} : { ...atuais, [campo]: undefined }));
  }

  function reescrever(mudanca: (atuais: ValoresDoConvenio) => ValoresDoConvenio) {
    setEditados((atuais) => mudanca(atuais ?? iniciais));
  }

  return { valores: editados ?? iniciais, erros, setErros, mudar, reescrever };
}

function comVazio(atual: string, opcoes: readonly (readonly [string, string])[]) {
  return atual === "" ? [["", "Escolha"] as const, ...opcoes] : opcoes;
}

/** Valor gravado fora do catálogo entra na lista: `<select>` sem a opção mostraria outra. */
export function opcoesDeCarteira(regras: RegrasDoConvenio, atual: string) {
  const conhecida = atual === "" || regras.carteiras.includes(atual);
  const carteiras = conhecida ? regras.carteiras : [atual, ...regras.carteiras];
  return comVazio(
    atual,
    carteiras.map((carteira) => [carteira, carteira] as const),
  );
}

export function opcoesDeCNAB(atual: string) {
  return comVazio(atual, [
    ["240", "240"],
    ["400", "400"],
  ]);
}

/** O nome dos campos que a tela mostra agora, para `errosDoFormulario`. */
export function camposNaTela(
  regras: RegrasDoConvenio,
  modo: string,
  gravado?: CamposOpcionais,
): CampoDoConvenio[] {
  const opcionais = camposDoBanco(regras, gravado);

  return [
    "numero",
    "descricao",
    "carteira",
    "variacao",
    "padrao_cnab",
    "modo_registro",
    ...(usaWebService(modo) ? (["tipo_webservice"] as const) : []),
    ...(opcionais.contrato ? (["numero_contrato"] as const) : []),
    ...(opcionais.remessa ? (["numero_remessa"] as const) : []),
    ...(opcionais.byte ? (["byte_nosso_numero"] as const) : []),
  ];
}

const ESQUEMA = z.object({
  numero: z.string().trim().min(1, "Informe o número."),
  carteira: z.string().trim().min(1, "Escolha a carteira."),
  variacao: z.string().trim(),
  descricao: z.string().trim(),
  modo_registro: z.enum(["manual", "automatico_van", "webservice", "misto"]),
  padrao_cnab: z.enum(["", "240", "400"]),
  tipo_webservice: z.string().trim(),
  numero_contrato: z.string().trim(),
  numero_remessa: z.string().trim(),
  reiniciar_remessa_diariamente: z.boolean(),
  byte_nosso_numero: z
    .string()
    .trim()
    .regex(/^\d?$/, "Use um dígito."),
});

type ConvenioValidado = z.output<typeof ESQUEMA>;

function lerValores(
  valores: ValoresDoConvenio,
): { ok: true; convenio: ConvenioValidado } | { ok: false; erros: ErrosDoConvenio } {
  const resultado = ESQUEMA.safeParse(valores);
  if (resultado.success) return { ok: true, convenio: resultado.data };

  const erros: ErrosDoConvenio = {};
  for (const problema of resultado.error.issues) {
    const campo = problema.path[0];
    if (typeof campo === "string" && campo in valores && !erros[campo as CampoDoConvenio]) {
      erros[campo as CampoDoConvenio] = problema.message;
    }
  }
  return { ok: false, erros };
}

/**
 * As regras do banco, no mesmo sentido de `ValidarConvenioContraOBanco`.
 *
 * Lê os valores como digitados, e não o resultado do esquema: tudo o que falta aparece de
 * uma vez, em vez de uma rodada para o número e outra para o contrato.
 */
function defeitosDoCatalogo(
  valores: ValoresDoConvenio,
  regras: RegrasDoConvenio,
): ErrosDoConvenio {
  const erros: ErrosDoConvenio = {};
  const webService = usaWebService(valores.modo_registro);

  if (regras.exigeCNAB && valores.padrao_cnab === "") {
    erros.padrao_cnab = "Escolha o padrão CNAB.";
  } else if (
    webService &&
    regras.cnabComWebService !== "" &&
    valores.padrao_cnab !== "" &&
    valores.padrao_cnab !== regras.cnabComWebService
  ) {
    erros.padrao_cnab = `Com WebService, o padrão é ${regras.cnabComWebService}.`;
  }
  if (regras.exigeRemessa && valores.numero_remessa.trim() === "") {
    erros.numero_remessa = "Informe o número da remessa.";
  }
  if (regras.exigeContrato && webService && valores.numero_contrato.trim() === "") {
    erros.numero_contrato = "Informe o número do contrato.";
  }
  return erros;
}

function semErros(erros: ErrosDoConvenio): boolean {
  return Object.keys(erros).length === 0;
}

export type ResultadoDaCriacao =
  | { ok: true; convenio: ConvenioValidado }
  | { ok: false; erros: ErrosDoConvenio };

/** A descrição é pedida só aqui (design, questão aberta 3): a API não a exige. */
export function validarCriacao(
  valores: ValoresDoConvenio,
  regras: RegrasDoConvenio,
): ResultadoDaCriacao {
  const lido = lerValores(valores);
  const erros = { ...defeitosDoCatalogo(valores, regras), ...(lido.ok ? {} : lido.erros) };
  if (valores.descricao.trim() === "") erros.descricao = "Informe a descrição.";

  return lido.ok && semErros(erros) ? lido : { ok: false, erros };
}

export type ResultadoDoProximo = { ok: true; numero: number } | { ok: false; erro: string };

/**
 * O nosso número inicial. Vazio não vira 1 aqui, embora a API o aceite: o campo nasce
 * com o valor à vista, e apagá-lo não é uma escolha que a tela deva completar.
 */
export function lerProximoNossoNumero(
  texto: string,
  regras: RegrasDoConvenio,
): ResultadoDoProximo {
  const { maxDigitosNossoNumero: maximo } = regras;
  const resultado = z
    .string()
    .trim()
    .min(1, "Informe o próximo nosso número.")
    .regex(/^\d+$/, "Use um número inteiro.")
    .transform(Number)
    .refine((numero) => numero >= 1, "Mínimo de 1.")
    .refine(Number.isSafeInteger, "Valor acima do máximo.")
    .refine(
      (numero) => maximo === 0 || String(numero).length <= maximo,
      `Este banco aceita até ${maximo} dígitos.`,
    )
    .safeParse(texto);

  return resultado.success
    ? { ok: true, numero: resultado.data }
    : { ok: false, erro: resultado.error.issues[0]?.message ?? "Valor inválido." };
}

/** Vazio não vai: campo que o banco não usa não chega à API, que o recusaria. */
export function corpoDaCriacao(convenio: ConvenioValidado, proximo: number): ConvenioNovo {
  const webService = usaWebService(convenio.modo_registro);

  return {
    numero: convenio.numero,
    carteira: convenio.carteira,
    modo_registro: convenio.modo_registro,
    registro_instantaneo: webService,
    reiniciar_remessa_diariamente: convenio.reiniciar_remessa_diariamente,
    proximo_nosso_numero: proximo,
    ...(convenio.descricao !== "" && { descricao: convenio.descricao }),
    ...(convenio.variacao !== "" && { variacao: convenio.variacao }),
    ...(convenio.padrao_cnab !== "" && { padrao_cnab: convenio.padrao_cnab }),
    ...(webService && convenio.tipo_webservice !== "" && {
      tipo_webservice: convenio.tipo_webservice,
    }),
    ...(convenio.numero_contrato !== "" && { numero_contrato: convenio.numero_contrato }),
    ...(convenio.numero_remessa !== "" && { numero_remessa: convenio.numero_remessa }),
    ...(convenio.byte_nosso_numero !== "" && { byte_nosso_numero: convenio.byte_nosso_numero }),
  };
}

/* `descricao` e o reinício da remessa não passam pelo catálogo, e o resto passa (design D10). */
const FORA_DO_CATALOGO: ReadonlySet<string> = new Set([
  "descricao",
  "reiniciar_remessa_diariamente",
]);

function tocaCatalogo(corpo: AlteracaoDeConvenio): boolean {
  return Object.keys(corpo).some((campo) => !FORA_DO_CATALOGO.has(campo));
}

/**
 * O corpo da edição: só o que MUDOU.
 *
 * Campo repetido não é campo tocado: mandar o formulário inteiro faria um convênio
 * anterior ao reforço cair no catálogo por uma troca de descrição.
 *
 * O espelho acompanha o modo que muda. Fora disso, vai só quando a alteração já passa
 * pelo catálogo e o gravado contradiz o modo: o convênio antigo em `automatico_van` com
 * `true` seria recusado por um campo que a tela não deixa mexer.
 */
function corpoDaEdicao(
  antes: Convenio,
  depois: ConvenioValidado,
  janelaAberta: boolean,
): AlteracaoDeConvenio {
  const gravado = valoresDe(antes);
  const corpo: AlteracaoDeConvenio = {};

  if (janelaAberta) {
    if (depois.numero !== gravado.numero) corpo.numero = depois.numero;
    if (depois.carteira !== gravado.carteira) corpo.carteira = depois.carteira;
    if (depois.variacao !== gravado.variacao) corpo.variacao = depois.variacao;
    if (depois.byte_nosso_numero !== gravado.byte_nosso_numero) {
      corpo.byte_nosso_numero = depois.byte_nosso_numero;
    }
  }

  if (depois.descricao !== gravado.descricao) corpo.descricao = depois.descricao;
  if (depois.modo_registro !== gravado.modo_registro) corpo.modo_registro = depois.modo_registro;
  if (depois.padrao_cnab !== "" && depois.padrao_cnab !== gravado.padrao_cnab) {
    corpo.padrao_cnab = depois.padrao_cnab;
  }
  if (depois.tipo_webservice !== gravado.tipo_webservice) {
    corpo.tipo_webservice = depois.tipo_webservice;
  }
  if (depois.numero_contrato !== gravado.numero_contrato) {
    corpo.numero_contrato = depois.numero_contrato;
  }
  if (depois.numero_remessa !== gravado.numero_remessa) {
    corpo.numero_remessa = depois.numero_remessa;
  }
  if (depois.reiniciar_remessa_diariamente !== gravado.reiniciar_remessa_diariamente) {
    corpo.reiniciar_remessa_diariamente = depois.reiniciar_remessa_diariamente;
  }

  const espelho = usaWebService(depois.modo_registro);
  const contradiz = espelho !== (antes.registro_instantaneo === true);
  if (corpo.modo_registro !== undefined || (tocaCatalogo(corpo) && contradiz)) {
    corpo.registro_instantaneo = espelho;
  }

  return corpo;
}

export type ResultadoDaEdicao =
  | { ok: true; corpo: AlteracaoDeConvenio }
  | { ok: false; erros: ErrosDoConvenio };

/**
 * O catálogo só confere a alteração que o toca (spec "Alteração que não toca o catálogo
 * não revalida"): sem isso, o convênio anterior ao reforço nem poderia ser renomeado.
 */
export function validarEdicao(
  antes: Convenio,
  valores: ValoresDoConvenio,
  regras: RegrasDoConvenio,
  janelaAberta: boolean,
): ResultadoDaEdicao {
  const lido = lerValores(valores);

  // O que o esquema recusa é campo tocado: nenhum valor gravado reprova nele.
  if (!lido.ok) {
    return { ok: false, erros: { ...defeitosDoCatalogo(valores, regras), ...lido.erros } };
  }

  const corpo = corpoDaEdicao(antes, lido.convenio, janelaAberta);
  const erros = tocaCatalogo(corpo) ? defeitosDoCatalogo(valores, regras) : {};

  return semErros(erros) ? { ok: true, corpo } : { ok: false, erros };
}

/* ── A janela de alteração ───────────────────────────────────────────────── */

export function alteraOQueViaja(antes: Convenio, valores: ValoresDoConvenio): boolean {
  const gravado = valoresDe(antes);
  return CAMPOS_QUE_VIAJAM.some((campo) => valores[campo].trim() !== gravado[campo]);
}

/** Volta ao gravado o que a janela fechou: valor recusado em campo travado parece salvo. */
export function comOQueViajaGravado(
  valores: ValoresDoConvenio,
  antes: Convenio,
): ValoresDoConvenio {
  const gravado = valoresDe(antes);
  return {
    ...valores,
    numero: gravado.numero,
    carteira: gravado.carteira,
    variacao: gravado.variacao,
    byte_nosso_numero: gravado.byte_nosso_numero,
  };
}

/*
 * As duas recusas de alteração têm saídas opostas (design D9), e a tela responde de
 * jeitos diferentes: a janela fechada é definitiva, e trava os campos; o boleto a
 * caminho passa quando a fila esvazia, e o que foi digitado fica para repetir.
 */
export function janelaFechada(falha: unknown): boolean {
  return falha instanceof FalhaDaApi && falha.codigo === "alteracao_bancaria_fechada";
}

export function emissaoEmAndamento(falha: unknown): boolean {
  return falha instanceof FalhaDaApi && falha.codigo === "emissao_em_andamento";
}

/**
 * A janela na tela de leitura. O sinal ausente vale como fechada: na dúvida, a tela não
 * promete uma alteração que a API pode recusar.
 */
export function situacaoDaJanela(convenio: Convenio): { texto: string; dica?: string } {
  if (convenio.alteracao_bancaria_permitida !== true) {
    return {
      texto: "Fechada",
      dica: "Já há boleto com estes dados. Número, carteira, variação e byte só mudam no banco e com a TecnoSpeed.",
    };
  }
  if (convenio.emissao_a_caminho === true) {
    return {
      texto: "Boleto a caminho",
      dica: "Número, carteira, variação e byte não mudam enquanto a fila não esvaziar.",
    };
  }
  return { texto: "Aberta" };
}
